import React, { useState, useEffect } from "react";
import { auth } from "../firebase";
import { logFirebaseConfig, checkFirebaseAuth } from "../utils/firebaseCheck";
import { createUserWithEmailAndPassword } from "firebase/auth";

function FirebaseDiagnostic() {
  const [diagnostic, setDiagnostic] = useState({
    config: null,
    authCheck: null,
    testResult: null,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    runDiagnostics();
  }, []);

  const runDiagnostics = async () => {
    console.log("🔍 Running Firebase Diagnostics...");
    
    // Check configuration
    const config = {
      projectId: auth?.app?.options?.projectId || "Not found",
      authDomain: auth?.app?.options?.authDomain || "Not found",
      apiKey: auth?.app?.options?.apiKey ? `${auth.app.options.apiKey.substring(0, 15)}...` : "Not found",
    };
    
    // Check auth
    const authCheck = await checkFirebaseAuth();
    
    setDiagnostic({
      config,
      authCheck,
      testResult: null,
    });
    
    logFirebaseConfig();
  };

  const testRegistration = async () => {
    setLoading(true);
    const testEmail = `test-${Date.now()}@example.com`;
    const testPassword = "test123456";
    
    try {
      const result = await createUserWithEmailAndPassword(auth, testEmail, testPassword);
      setDiagnostic(prev => ({
        ...prev,
        testResult: {
          success: true,
          message: `✅ Test registration successful! User ID: ${result.user.uid}`,
        },
      }));
      // Clean up test user
      await result.user.delete();
    } catch (error) {
      setDiagnostic(prev => ({
        ...prev,
        testResult: {
          success: false,
          message: `❌ Test failed: ${error.code} - ${error.message}`,
          error: error.code,
        },
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}>
      <h1>Firebase Authentication Diagnostic</h1>
      
      <div style={{ 
        backgroundColor: "#f5f5f5", 
        padding: "20px", 
        borderRadius: "8px", 
        marginBottom: "20px" 
      }}>
        <h2>Configuration Status</h2>
        <div style={{ marginLeft: "20px" }}>
          <p><strong>Project ID:</strong> {diagnostic.config?.projectId || "Checking..."}</p>
          <p><strong>Auth Domain:</strong> {diagnostic.config?.authDomain || "Checking..."}</p>
          <p><strong>API Key:</strong> {diagnostic.config?.apiKey || "Checking..."}</p>
        </div>
      </div>

      <div style={{ 
        backgroundColor: diagnostic.authCheck?.success ? "#e8f5e9" : "#ffebee", 
        padding: "20px", 
        borderRadius: "8px", 
        marginBottom: "20px" 
      }}>
        <h2>Authentication Check</h2>
        <p>
          {diagnostic.authCheck?.success ? "✅" : "❌"} {diagnostic.authCheck?.message || "Checking..."}
        </p>
        {diagnostic.authCheck?.error && (
          <div style={{ marginTop: "10px", padding: "10px", backgroundColor: "#fff", borderRadius: "4px" }}>
            <strong>Error Details:</strong>
            <pre style={{ fontSize: "12px", overflow: "auto" }}>
              {JSON.stringify(diagnostic.authCheck.error, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {diagnostic.testResult && (
        <div style={{ 
          backgroundColor: diagnostic.testResult.success ? "#e8f5e9" : "#ffebee", 
          padding: "20px", 
          borderRadius: "8px", 
          marginBottom: "20px" 
        }}>
          <h2>Test Registration</h2>
          <p>{diagnostic.testResult.message}</p>
          {diagnostic.testResult.error && (
            <div style={{ marginTop: "10px" }}>
              <strong>Error Code:</strong> {diagnostic.testResult.error}
              {diagnostic.testResult.error === "auth/configuration-not-found" && (
                <div style={{ marginTop: "10px", padding: "10px", backgroundColor: "#fff3cd", borderRadius: "4px" }}>
                  <strong>⚠️ Action Required:</strong>
                  <ol style={{ marginTop: "10px" }}>
                    <li>Go to <a href="https://console.firebase.google.com/" target="_blank" rel="noopener noreferrer">Firebase Console</a></li>
                    <li>Select project: <strong>{auth?.app?.options?.projectId || "your Firebase project"}</strong></li>
                    <li>Navigate to: <strong>Authentication → Sign-in method</strong></li>
                    <li>Enable: <strong>Email/Password</strong> provider</li>
                    <li>Click <strong>Save</strong></li>
                    <li>Refresh this page and test again</li>
                  </ol>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <button
        onClick={testRegistration}
        disabled={loading}
        style={{
          padding: "12px 24px",
          backgroundColor: "#44C3C4",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: loading ? "not-allowed" : "pointer",
          fontSize: "16px",
          opacity: loading ? 0.6 : 1,
        }}
      >
        {loading ? "Testing..." : "Test Registration"}
      </button>

      <button
        onClick={runDiagnostics}
        style={{
          padding: "12px 24px",
          backgroundColor: "#6c757d",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
          fontSize: "16px",
          marginLeft: "10px",
        }}
      >
        Refresh Diagnostics
      </button>

      <div style={{ marginTop: "30px", padding: "20px", backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
        <h3>📋 Quick Checklist</h3>
        <ul>
          <li>✅ Firebase project ID: <strong>{auth?.app?.options?.projectId || "not loaded"}</strong></li>
          <li>✅ Email/Password enabled in Firebase Console</li>
          <li>✅ No browser console errors</li>
          <li>✅ Test registration works</li>
        </ul>
      </div>
    </div>
  );
}

export default FirebaseDiagnostic;

